
import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

interface ParseProgress {
  current: number;
  total: number;
  stage: 'reading' | 'parsing' | 'complete';
}

interface FarmPriceUploadStatusProps {
  file: File | null;
  error: string;
  isProcessing: boolean;
  progress?: ParseProgress;
}

const FarmPriceUploadStatus: React.FC<FarmPriceUploadStatusProps> = ({ 
  file, 
  error, 
  isProcessing, 
  progress 
}) => {
  const getProgressText = () => {
    if (!progress) return 'Processing...';
    
    switch (progress.stage) {
      case 'reading':
        return 'Reading Excel file...';
      case 'parsing':
        return `Parsing data... ${progress.current}/${progress.total} rows`;
      case 'complete':
        return 'Processing complete!';
      default:
        return 'Processing...';
    }
  };

  const getProgressPercentage = () => {
    if (!progress || progress.total === 0) return 0;
    if (progress.stage === 'reading') return 10;
    if (progress.stage === 'complete') return 100;
    return 10 + (progress.current / progress.total) * 90;
  };

  return (
    <>
      {file && !isProcessing && !error && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Selected file: {file.name} ({Math.round(file.size / 1024)} KB)
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p>{error}</p>
              <p className="text-xs text-muted-foreground">
                If the upload was interrupted, you can try again. The system will restart from the beginning.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {isProcessing && (
        <div className="space-y-4 py-4">
          <div className="flex items-center gap-3">
            <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">{getProgressText()}</p>
              {progress && progress.total > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Progress: {progress.current} of {progress.total} records
                </p>
              )}
            </div>
          </div>
          
          <Progress value={getProgressPercentage()} className="w-full" />
          
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Please don't close this window while processing. If the upload crashes, 
              you can restart by selecting the file again.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {progress?.stage === 'complete' && !isProcessing && !error && (
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            File processed successfully! {progress.total} records ready for upload.
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};

export default FarmPriceUploadStatus;
